import { hashBlobCrc32 } from '../utils/crc32';

interface Pending {
  resolve: (crc32: string) => void;
  reject: (err: Error) => void;
}

/** `undefined` = not tried yet; `null` = unavailable, hash on the main thread. */
let worker: Worker | null | undefined;
let nextId = 0;
const pending = new Map<number, Pending>();

function getWorker(): Worker | null {
  if (worker !== undefined) return worker;
  if (typeof Worker === 'undefined') return (worker = null);
  try {
    const w = new Worker(new URL('./crc32.worker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (event: MessageEvent<{ id: number; crc32?: string; error?: string }>) => {
      const { id, crc32, error } = event.data;
      const entry = pending.get(id);
      if (!entry) return;
      pending.delete(id);
      if (crc32) entry.resolve(crc32);
      else entry.reject(new Error(error ?? 'Hashing failed'));
    };
    w.onerror = () => {
      // A worker that can't start (CSP, old browser) shouldn't block uploads:
      // fail what it held and hash on the main thread from now on.
      worker = null;
      for (const entry of pending.values()) entry.reject(new Error('Hash worker failed'));
      pending.clear();
    };
    return (worker = w);
  } catch {
    return (worker = null);
  }
}

/** CRC32 of a file (S3's base64 encoding), computed in a Web Worker when possible. */
export function hashFileCrc32(file: File): Promise<string> {
  const w = getWorker();
  if (!w) return hashBlobCrc32(file);
  const id = nextId++;
  return new Promise<string>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    w.postMessage({ id, file });
  }).catch(() => hashBlobCrc32(file));
}
